import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
} from '@travellegacy/nexops-design-system';
import { Activity, Timer, ExternalLink, ShieldAlert } from 'lucide-react';
import type { ActiveDemand } from '../api/demands.api';
import { useSLAs } from '../hooks/useDemands';
import { formatDurationClock } from '../utils/format-duration';
import { buildNotionPageUrl } from '../utils/notion-url';

interface Props {
  demands: ActiveDemand[];
  isLoading: boolean;
  /**
   * Threshold em ms para uma demanda ser considerada "travada" (aging vermelho).
   * Vindo do overview (DEFAULT_STUCK_HOURS = 8h no backend). Se não passado,
   * usa 8h como fallback local.
   */
  stuckThresholdMs?: number;
}

/**
 * Semáforo de aging: verde (< warning), amarelo (>= warning e < target) e
 * vermelho (>= target). Se o tipo tiver SLA configurado, usa targetMs/warningMs
 * do SLA; senão cai no threshold global (metade → warning, threshold → stuck).
 */
function getAgingTone(
  elapsedMs: number,
  target: number,
  warning: number
): { color: string; label: 'ok' | 'warn' | 'stuck' } {
  if (elapsedMs >= target) {
    return { color: 'text-red-600 dark:text-red-400', label: 'stuck' };
  }
  if (elapsedMs >= warning) {
    return { color: 'text-amber-600 dark:text-amber-400', label: 'warn' };
  }
  return { color: 'text-emerald-600 dark:text-emerald-400', label: 'ok' };
}

const DEFAULT_STUCK_MS = 8 * 60 * 60 * 1000;

/**
 * Card com demandas atualmente em andamento e cronômetro em tempo real.
 * O contador roda localmente (setInterval 1s) para não sobrecarregar a API.
 * Cada item é clicável e abre a demanda correspondente no Notion.
 */
export function ActiveDemandsCard({ demands, isLoading, stuckThresholdMs }: Props) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const threshold = stuckThresholdMs ?? DEFAULT_STUCK_MS;

  const slasQuery = useSLAs();
  const slaByType = useMemo(() => {
    const map = new Map<string, { targetMs: number; warningMs: number | null }>();
    for (const s of slasQuery.data?.data ?? []) {
      if (s.enabled) {
        map.set(s.type, { targetMs: s.targetMs, warningMs: s.warningMs });
      }
    }
    return map;
  }, [slasQuery.data]);

  useEffect(() => {
    if (demands.length === 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [demands.length]);

  function openInNotion(notionPageId: string) {
    const url = buildNotionPageUrl(notionPageId);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  function openDetail(id: string) {
    navigate(`/demands/${id}`);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-emerald-500" />
          Demandas em andamento agora
          <Badge variant="outline">{demands.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : demands.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma demanda em andamento no momento.</p>
        ) : (
          <ul className="divide-y divide-border">
            {demands.map((d) => {
              const startedAtMs = d.openCycle
                ? new Date(d.openCycle.startedAt).getTime()
                : d.firstStartedAt
                  ? new Date(d.firstStartedAt).getTime()
                  : now;
              const elapsed = Math.max(0, now - startedAtMs);

              // Se o tipo tiver SLA, ele sobrepõe o threshold global.
              const sla = d.type ? slaByType.get(d.type) : undefined;
              const target = sla ? sla.targetMs : threshold;
              const warning = sla
                ? sla.warningMs ?? target / 2
                : threshold / 2;
              const tone = getAgingTone(elapsed, target, warning);
              const stuckTitle = sla
                ? `Fora do SLA de ${Math.round(target / 3_600_000)}h — ver detalhes`
                : `Travada há mais de ${Math.round(target / 3_600_000)}h — ver detalhes`;

              return (
                <li
                  key={d.id}
                  onClick={() => openDetail(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDetail(d.id);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                  aria-label={`Ver detalhes de "${d.title}"`}
                  title={tone.label === 'stuck' ? stuckTitle : 'Ver detalhes'}
                  className="flex cursor-pointer items-center justify-between gap-4 rounded-md px-2 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate font-medium">
                      {d.title}
                      {d.type && (
                        <Badge variant="info" className="ml-1 font-normal">
                          {d.type}
                        </Badge>
                      )}
                      {tone.label === 'stuck' &&
                        (sla ? (
                          <Badge
                            variant="error"
                            className="ml-1 font-normal inline-flex items-center gap-1"
                          >
                            <ShieldAlert className="h-3 w-3" />
                            Fora do SLA
                          </Badge>
                        ) : (
                          <Badge variant="error" className="ml-1 font-normal">
                            Travada
                          </Badge>
                        ))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {d.responsibleName} · ciclo #{d.cyclesCount}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center gap-2 font-mono text-sm tabular-nums ${tone.color}`}
                    >
                      <Timer className="h-4 w-4" />
                      {formatDurationClock(elapsed)}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInNotion(d.notionPageId);
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Abrir "${d.title}" no Notion`}
                      title="Abrir no Notion"
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
